/**
 * Design tokens — single source of truth for Tailwind + Ant Design.
 * palette = raw values; color/radius/space/fontSize/shadow = semantic.
 */

const palette = {
  // core
  brand: "#25add5",
  brandDark: "#23a0a8",
  primary: "#1565d8",
  black: "#000000",
  white: "#ffffff",
  darkBlack: "#333333",

  // text / icon
  textMuted: "#696f79",
  footer: "#8a8a8a",
  loginFooter: "#8692a6",
  link: "#095aa4",
  disabled: "#a3a3a3",
  icon: "#b8b8b8",
  iconAction: "#a1b0b1",
  iconActionHover: "#58b2cd",
  navInactive: "#97a8a9",

  // borders / greys
  border: "#d0d0d0",
  borderStrong: "#c7c7c7",
  borderSubtle: "#dfe2e8",
  grey: "#cccccc",
  greyBg: "#e5e5e5",
  lightGrey: "#f2f2f2",
  cardBg: "#f4f4f4",
  inputFill: "#f1f1f1",
  loginDivider: "#e8e3e3",
  subHeaderSide: "#dbdbdb",

  // surfaces
  surface: "#f2f5f7",
  imageWrapper: "#d9dae4",
  tableHeader: "#eeeeee",
  tableCell: "#444444",
  tableBorder: "#d9d7d7",
  tableRowHover: "#f0f8f9",

  // placeholders
  placeholderMuted: "#a0a0a0",
  placeholderHover: "#494949",

  // buttons / actions
  btnSecondary: "#10a2ce",
  cancel: "#717171",
  cancelBorder: "#dfe2e8",
  danger: "#ff4d4f",

  // status
  success: "#b7eb8f",
  successBg: "#f6ffed",
  error: "#ffccc7",
  errorBg: "#fff2f0",
  alertText: "#172b4d",
  alertBg: "#ffebe6",
  calendarHeader: "#27adb6",

  // overlays
  blackAlpha19: "rgba(0, 0, 0, 0.19)",
  blackAlpha11: "rgba(0, 0, 0, 0.11)"
};

const color = {
  brand: palette.brand,
  brandDark: palette.brandDark,
  primary: palette.primary,
  link: palette.link,
  danger: palette.danger,

  text: {
    default: palette.black,
    muted: palette.textMuted,
    disabled: palette.disabled,
    inverse: palette.white,
    footer: palette.footer,
    loginFooter: palette.loginFooter,
    table: palette.tableCell
  },

  border: {
    DEFAULT: palette.border,
    default: palette.border,
    strong: palette.borderStrong,
    subtle: palette.borderSubtle
  },

  background: {
    container: palette.white,
    surface: palette.surface,
    muted: palette.lightGrey,
    input: palette.inputFill,
    card: palette.cardBg,
    grey: palette.greyBg,
    tableHeader: palette.tableHeader
  },

  placeholder: {
    DEFAULT: palette.border,
    default: palette.border,
    muted: palette.placeholderMuted,
    hover: palette.placeholderHover
  },

  icon: {
    DEFAULT: palette.icon,
    default: palette.icon,
    action: palette.iconAction,
    actionHover: palette.iconActionHover,
    navInactive: palette.navInactive
  },

  success: {
    DEFAULT: palette.success,
    bg: palette.successBg
  },

  error: {
    DEFAULT: palette.error,
    bg: palette.errorBg,
    strong: palette.danger
  },

  alert: {
    text: palette.alertText,
    bg: palette.alertBg
  },

  cancel: {
    DEFAULT: palette.cancel,
    border: palette.cancelBorder
  }
};

const radius = {
  sm: 4,
  md: 5,
  lg: 6
};

const space = {
  inputInline: 12,
  inputHeight: 32,
  0: "0",
  1: "0.25rem",
  2: "0.5rem",
  3: "0.75rem",
  4: "1rem",
  5: "1.25rem",
  6: "1.5rem",
  8: "2rem",
  10: "2.5rem",
  12: "3rem",
  16: "4rem"
};

const fontSize = {
  xs: "12px",
  sm: "14px",
  base: "16px",
  lg: "18px",
  xl: "20px",
  "2xl": "24px"
};

const fontFamily = {
  sans: ["OpenSans", "sans-serif"],
  inter: ["Inter", "sans-serif"],
  openSansSemibold: ["OpenSansSemiBold", "sans-serif"],
  openSansBold: ["OpenSansBold", "sans-serif"]
};

const breakpoint = {
  xs: { max: "575px" }, // small mobile
  sm: "576px", // mobile
  md: "768px", // tablet
  lg: "992px", // desktop
  xl: "1200px", // wide desktop
  xxl: "1600px", // extra wide desktop
}; 
const shadow = {
  inputFocus: `0 4px 10px 3px ${palette.blackAlpha11}`
};

const input = {
  heightSM: 24,
  heightMD: 32,
  heightFilled: 36,
  heightLG: 44,
  filledBg: palette.inputFill,
  placeholder: palette.border
};

module.exports = {
  palette,
  color,
  radius,
  space,
  fontSize,
  fontFamily,
  breakpoint,
  shadow,
  input
};
